import { useCallback, useEffect, useState } from 'react';
import type { OrderDetailDTO, OrderStatus } from '@scan2eat/shared';
import { formatMoney, ORDER_TRANSITIONS } from '@scan2eat/shared';
import { apiFetch } from '../api/client.js';
import { labels } from '../labels.js';
import { CancelDialog } from './CancelDialog.js';
import { ErrorState } from './EmptyState.js';
import { Icon } from './Icon.js';
import { Modal } from './Modal.js';
import { StatusBadge } from './StatusBadge.js';
import styles from './OrderDetailPanel.module.css';

interface Props {
  orderId: string;
  onClose: () => void;
  /** Absent: read-only panel, as in the history. */
  onTransition?: (orderId: string, action: string, note?: string) => void | Promise<void>;
}

export function orderActionKeys(status: OrderStatus): string[] {
  return Object.keys(ORDER_TRANSITIONS[status]);
}

const time = (iso: string) => new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });

/**
 * Panneau lateral de detail d'une commande. Partage entre le kanban et
 * l'historique : le personnel ne doit jamais perdre la liste de vue.
 * Voir docs/15-ui-ux.md, section 4.3.
 */
export function OrderDetailPanel({ orderId, onClose, onTransition }: Props) {
  const [order, setOrder] = useState<OrderDetailDTO | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);

  const fetchOrder = useCallback(() => {
    setLoading(true);
    setError(false);
    return apiFetch<OrderDetailDTO>(`/admin/orders/${orderId}`)
      .then(setOrder)
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [orderId]);

  useEffect(() => { fetchOrder(); }, [fetchOrder]);

  const run = (action: string, note?: string) =>
    Promise.resolve(onTransition?.(orderId, action, note)).then(fetchOrder);

  // Encaisser suppose un paiement declare par le convive : sans lui, l'API
  // refuse. Le panneau n'offre donc que ce que la carte offre. docs/06, section 10.
  // Pendant un encaissement chez le prestataire, la carte n'offre rien : c'est
  // lui qui tranche (docs/adr/0018). Le panneau suit la meme regle.
  const actions = order
    ? orderActionKeys(order.status).filter(
        (a) =>
          a !== 'COMPLETE' ||
          (order.payment?.status === 'DECLARED' && !order.payment.awaitingProvider),
      )
    : [];
  const primary = actions.find((a) => a !== 'CANCEL');
  const secondary = actions.filter((a) => a !== primary && a !== 'CANCEL');
  const canCancel = actions.includes('CANCEL');

  return (
    <>
      <Modal
        variant="drawer"
        onClose={onClose}
        title={order ? labels.order.number(order.number) : labels.detail.title}
        aside={order && (
          <div className={styles.badges}>
            <StatusBadge status={order.status} />
          </div>
        )}
        description={order && (
          <span className={styles.meta}>
            <span><Icon name="table" size={16} />{labels.app.tableLabel(order.table.label)}{order.table.zone ? ` · ${order.table.zone}` : ''}</span>
            <span><Icon name="clock" size={16} />{labels.detail.placedAt(time(order.placedAt))}</span>
          </span>
        )}
        footer={onTransition && order && actions.length > 0 ? (
          <>
            {primary && (
              <button type="button" className="btn-primary grow" onClick={() => void run(primary)}>
                {labels.actions[primary as keyof typeof labels.actions]}
              </button>
            )}
            {secondary.map((a) => (
              <button key={a} type="button" className="btn-ghost" onClick={() => void run(a)}>
                {labels.actions[a as keyof typeof labels.actions]}
              </button>
            ))}
            {canCancel && (
              <button type="button" className="btn-danger" onClick={() => setCancelOpen(true)}>
                {labels.actions.CANCEL}
              </button>
            )}
          </>
        ) : undefined}
      >
        {loading && !order ? (
          <div className="stack">
            <div className="skeleton" style={{ height: '9rem' }} />
            <div className="skeleton" style={{ height: '5rem' }} />
            <div className="skeleton" style={{ height: '10rem' }} />
          </div>
        ) : error || !order ? (
          <ErrorState onRetry={fetchOrder} text={labels.errors.generic} />
        ) : (
          <>
            <section className="card card--flush">
              <ul className="list">
                {order.items.map((item) => (
                  <li key={item.id} className={styles.line}>
                    <span className={styles.qty}>{item.quantity}</span>
                    <span className="grow">
                      <span className={styles.lineName}>{item.nameSnapshot}</span>
                      {item.note && <span className={styles.lineNote}>{item.note}</span>}
                    </span>
                    <span className="tabular">{formatMoney(item.lineTotalMinor, order.currency)}</span>
                  </li>
                ))}
              </ul>
              <dl className={styles.totals}>
                <div><dt>{labels.detail.subtotal}</dt><dd>{formatMoney(order.subtotalMinor, order.currency)}</dd></div>
                <div><dt>{labels.detail.tax}</dt><dd>{formatMoney(order.taxMinor, order.currency)}</dd></div>
                <div className={styles.grandTotal}><dt>{labels.detail.total}</dt><dd>{formatMoney(order.totalMinor, order.currency)}</dd></div>
              </dl>
            </section>

            {order.payment && (
              <section className={styles.callout}>
                <Icon name={order.payment.method === 'COUNTER' ? 'cash' : 'phone'} size={18} />
                <div className="grow">
                  <p className={styles.calloutTitle}>{labels.payment.methodLabels[order.payment.method]}</p>
                  <p className={styles.calloutText}>
                    {labels.payment.statusLabels[order.payment.status]}
                    {order.payment.payerPhone && <> · {labels.payment.payerPhone} <span className="mono">{order.payment.payerPhone}</span></>}
                    {order.payment.reference && <> · {labels.payment.reference} <span className="mono">{order.payment.reference}</span></>}
                  </p>
                </div>
              </section>
            )}

            {order.customerNote && (
              <section className={styles.callout}>
                <Icon name="info" size={18} />
                <div className="grow">
                  <p className={styles.calloutTitle}>{labels.detail.customerNote}</p>
                  <p className={styles.calloutText}>{order.customerNote}</p>
                </div>
              </section>
            )}

            {order.cancelReason && (
              <section className={`${styles.callout} ${styles.calloutDanger}`}>
                <Icon name="alert" size={18} />
                <div className="grow">
                  <p className={styles.calloutTitle}>{labels.status.CANCELLED}</p>
                  <p className={styles.calloutText}>{order.cancelReason}</p>
                </div>
              </section>
            )}

            <section>
              <h3 className="section-title" style={{ marginBottom: 'var(--space-3)' }}>{labels.detail.timeline}</h3>
              <ol className={styles.timeline}>
                {order.events.map((ev) => (
                  <li key={ev.id} className={styles.event}>
                    <span className={styles.eventDot} aria-hidden="true" />
                    <div className="grow">
                      <p className={styles.eventTitle}>{labels.status[ev.toStatus]}</p>
                      <p className={styles.eventMeta}>
                        {labels.detail.actor(ev.actor, ev.userName ?? null)}
                        {ev.fromStatus && ` · ${labels.detail.from(labels.status[ev.fromStatus])}`}
                      </p>
                      {ev.note && <p className={styles.eventNote}>{ev.note}</p>}
                    </div>
                    <time className={styles.eventTime} dateTime={ev.createdAt}>{time(ev.createdAt)}</time>
                  </li>
                ))}
              </ol>
            </section>
          </>
        )}
      </Modal>

      {cancelOpen && (
        <CancelDialog
          onCancel={() => setCancelOpen(false)}
          onConfirm={(note) => { setCancelOpen(false); void run('CANCEL', note); }}
        />
      )}
    </>
  );
}
